<template>
    <common-page
        :label="event?.fullTitle"
        :use-custom-content="true"
        page-class="event-width-dialog-standard"
        :extra-breadcrumb="extraBreadcrumb"
        :to="{ name: 'EventConfigurationTab' }"
        :full-height="route.name === 'EventDrawCourse'"
    >
        <q-page-sticky position="top-right" :offset="[20, 20]" v-if="event?.modified && isAuthenticated">
            <q-btn fab icon="save" color="primary" @click="saveEvent" :title="ts('saveToCloud')" />
        </q-page-sticky>

        <q-tabs v-model="tab" align="justify" dense active-color="primary">
            <q-route-tab
                name="EventConfigurationTab"
                :to="{ name: 'EventConfigurationTab' }"
                icon="event"
                :label="ts('eventInfoShortcut')"
            />
            <q-route-tab
                name="EventStartListTab"
                :to="{ name: 'EventStartListTab' }"
                icon="list"
                :label="ts('crewListShortcut')"
            />
            <q-route-tab
                name="EventNoticeboardTab"
                :to="{ name: 'EventNoticeboardTab' }"
                icon="info"
                :label="ts('noticeboard')"
            />
            <!-- A plain q-tab (not q-route-tab): the per-race sub-routes (round/heat/heats-division/results)
                 are flat sibling routes, so a route-tab would lose its active state on them. As a plain tab
                 whose name is driven from the route below, QTabs keeps it highlighted across all sub-routes. -->
            <q-tab
                v-for="race in event?.races"
                :key="race.id"
                :name="'EventRaceTab-' + race.race"
                icon="flag"
                :label="ts('raceNum', { num: race.race })"
                @click="router.push({ name: 'EventRaceTab', params: { raceId: race.race } })"
            />
            <q-route-tab
                v-if="!event?.isLongDistanceOnly || (event?.races.length === 0)"
                exact
                name="EventRaceTab"
                :to="{ name: 'EventRaceTab' }"
                icon="add"
                :label="ts(event?.races.length > 3 ? 'newRaceShortcut' : 'newRace')"
            />
            <q-route-tab
                name="EventResultsTab"
                :to="{ name: 'EventResultsTab' }"
                icon="check"
                :label="ts('resultsShortcut')"
            />
            <q-route-tab
                name="EventManagementTab"
                :to="{ name: 'EventManagementTab' }"
                icon="settings"
                :label="ts('manageEventShortcut')"
            />
        </q-tabs>

        <q-separator />

        <router-view
            v-slot="{ Component }"
            @log-in-and-update="emits('logInAndUpdate')"
            @save-event-to-cloud="saveEvent()"
        >
            <keep-alive>
                <div class="minh" :class="{ 'col column': route.name === 'EventDrawCourse' }">
                    <component :is="Component" :event="event" :class="{ 'col': route.name === 'EventDrawCourse' }" />
                </div>
            </keep-alive>
        </router-view>
    </common-page>
</template>

<script setup lang="ts">
// Purpose: solve handling one selected event (dashboard and all event navigation)

import CommonPage from '@/components/parts/commonPage.vue'
import { authenticate, checkAuth, showError } from '@/network/auth'
import { exportEvent } from '@/network/exporter'
import apiRequest from '@/network/httpClient'
import EventsRepo from '@/orm/repositories/EventsRepo'
import { ts } from '@/plugins/i18nFormat'
import InfoBox from '@dialogs/InfoBox.vue'
import LogIn from '@dialogs/LogIn.vue'
import { Defaults, Options } from '@model/Setting'
import SettingsRepo from '@repo/SettingsRepo'
import { useRepo } from 'pinia-orm'
import { QSpinnerGears, useQuasar } from 'quasar'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const quasar = useQuasar()

const emits = defineEmits(['logInAndUpdate'])

const isAuthenticated = computed(() => (useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken) !== ''))

useRepo(EventsRepo).selectEvent(String(route.params.eventId))

const event = computed(() => (useRepo(EventsRepo).getEvent(String(route.params.eventId), true)))

const extraBreadcrumb = computed(() => {
    if (route.name !== 'EventDrawCourse') return ''
    if (!route.params.course) return ts('raceTrack')
    return ts('courseTrack', { title: route.params.course })
})

const tab = ref(event.value.currentTab)

watch(tab, (tab) => {
    event.value.currentTab = tab
})

// The race tabs are plain q-tabs (see template), so their active state is not derived from the route.
// Drive the tab model from the route here so the active indicator (underline) lands on - and stays on -
// the current race tab across all of its sub-routes (round/heat/heats-division/results), incl. deep links.
watch(
    () => [route.name, route.params.raceId],
    () => {
        if (typeof route.name === 'string' && route.name.startsWith('EventRace') && route.params.raceId !== undefined)
            tab.value = 'EventRaceTab-' + route.params.raceId
    },
    { immediate: true },
)

async function logIn(event, attempts = 3, error = 0) {
    quasar.dialog({
        component: LogIn,
        componentProps: {
            errorMsg: showError(error),
        },
    }).onOk(async (payload) => {
        const lastError = await authenticate(payload.username, payload.password)

        if (!isAuthenticated.value) {
            if (attempts > 1)
                logIn(event, attempts - 1, lastError)
            else
                quasar.dialog({
                    component: InfoBox,
                    componentProps: {
                        message: ts('tooManyAttempts'),
                    },
                })
        } else {
            await performSaveEvent()
        }
    })
}

async function performSaveEvent() {
    const notif = quasar.notify({
        group: false,
        type: 'secondary',
        spinner: QSpinnerGears,
        message: ts('exportInProgress'),
        timeout: 0,
        multiLine: true,
        classes: 'q-pa-lg text-subtitle1',
    })

    const data = JSON.stringify(exportEvent(event.value))

    apiRequest(useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken)).post('/events/cloud', {
        'ctlId': event.value.ctlId,
        'eventTitle': event.value.title,
        'workFile': data,
        'scorers': event.value.scorerRegIds,
    })
        .then((response) => {
            if (response.status === 202) {
                event.value?.setModified(false)

                notif({
                    spinner: false,
                    icon: 'done',
                    message: ts('exportDone'),
                    timeout: 5000,
                    multiLine: true,
                    classes: 'q-pa-lg text-subtitle1',
                })
            } else {
                notif({
                    spinner: false,
                    color: 'negative',
                    icon: 'error',
                    message: ts('exportFail'),
                    timeout: 5000,
                    multiLine: true,
                    classes: 'q-pa-lg text-subtitle1',
                })

                console.log(response)
            }
        })
        .catch((error) => {
            notif({
                spinner: false,
                color: 'negative',
                icon: 'error',
                message: ts('exportFail'),
                timeout: 5000,
                multiLine: true,
                classes: 'q-pa-lg text-subtitle1',
            })

            console.log(error)
        })
}

async function saveEvent(event) {
    if (await checkAuth() !== 'auth') {
        await logIn(event)

        return
    }

    await performSaveEvent()
}
</script>

<style lang="sass" scoped>
.minh
    min-height: 300px
</style>

<style lang="sass">
.event-width-dialog-standard
    min-width: 1024px !important
</style>
